import './EventTile.scss';

import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import Badge from 'components/Badge/Badge';
import { ReactComponent as CrownIcon } from 'images/icons/Crown.svg';
import { DefaultRoutes } from 'Routes';
import TileOverlay from 'components/TileOverlay/TileOverlay';
import { TrackmanEventModel } from 'types/trackmanEvents';
import VideoThumbnail from 'components/VideoThumbnail/VideoThumbnail';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayjs from 'dayjs';
import { formatTimeLength } from 'utilities/formatDate';
import { observer } from 'mobx-react-lite';
import timezone from 'dayjs/plugin/timezone';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStores } from 'index';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export type Props = {
  eventItem: TrackmanEventModel;
  setIsModalOn?: (isOpen: boolean) => void;
  setModalData?: (data: { videoId: string; videoLabel: string }) => void;
};

export const EventTile = observer(({ eventItem, setIsModalOn, setModalData }: Props) => {
  const ps = useStores().profileStore;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isPremiumLocked = eventItem.premiumEvent && !ps.isUserMember;

  const overlayText = (
    <Trans i18nKey='PremiumEventOverlayInfo' ns='trackmanEvents'>
      Only <strong>Premium</strong> members can view this Event.&nbsp;
      <span className='learn-more' onClick={() => navigate(DefaultRoutes.Certifications)}>
        Learn more
      </span>
      about upgrading.
    </Trans>
  );

  const goToEventPageHandle = useCallback((url: string): void => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  }, []);

  const handleVideoModalClick = useCallback(() => {
    eventItem.videoId && setIsModalOn && setIsModalOn(true);
    eventItem.videoId && setModalData && setModalData({ videoId: eventItem.videoId, videoLabel: eventItem.title });
  }, [eventItem, setIsModalOn, setModalData]);

  const timeInfo =
    eventItem.startDate && !eventItem.recordingReady
      ? dayjs(eventItem.startDate).tz(dayjs.tz.guess()).format('MMM DD, h:mma z')
      : t('StreamedTimeInfo', { streamedInfo: dayjs().diff(dayjs(eventItem.startDate), 'days'), ns: 'trackmanEvents' });

  return (
    <div className='event-tile' onClick={() => !isPremiumLocked && handleVideoModalClick()}>
      <button
        className='event-tile-button'
        onClick={() =>
          !eventItem.videoId && !isPremiumLocked ? goToEventPageHandle(eventItem.videoUrl ? eventItem.videoUrl : eventItem.meetingUrl) : {}
        }
      >
        <div className='event-media'>
          {isPremiumLocked ? <TileOverlay color={'#EC691A'} overlayText={overlayText} /> : null}
          {eventItem.videoId ? (
            <VideoThumbnail
              video={eventItem.videoId}
              thumbnailSize='hqdefault'
              badgeText={eventItem.premiumEvent && !isPremiumLocked ? 'Premium' : ''}
              badgeIcon={eventItem.premiumEvent && !isPremiumLocked ? <CrownIcon /> : null}
            />
          ) : (
            <div className='image-wrapper'>
              <img src={eventItem.imageUrl ?? ''} alt={eventItem.title} />
              {eventItem.premiumEvent && !isPremiumLocked ? <Badge badgeText={'Premium'} badgeIcon={<CrownIcon />} /> : null}
            </div>
          )}
        </div>
        <div className='event-info'>
          {!eventItem.recordingReady ? <h3>{eventItem.locationType === 'Online' ? 'Online' : eventItem.location}</h3> : null}
          <div className='time'>
            {timeInfo}
            {eventItem.recordingReady && eventItem.videoLength ? (
              <span className='video-length'>{formatTimeLength(eventItem.videoLength)}</span>
            ) : null}
          </div>
          <div className='event-title'>
            {eventItem.title}
            {!eventItem.recordingReady ? <ArrowUpIcon /> : null}
          </div>
        </div>
      </button>
    </div>
  );
});

export default EventTile;
